import React from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useCart } from "@/context/CartContext";
import { formatPrice } from "@/lib/api";
import { fragranceAudience } from "@/lib/fragranceAudience";

const categoryLabels = {
  skincare: { it: "Skincare", en: "Skincare" },
  profumi: { it: "Profumi", en: "Fragrances" },
  "fragranze-ambiente": { it: "Home Fragrance", en: "Home Fragrances" },
};

const ProductCard = ({ product }) => {
  const { tl, t } = useLang();
  const { addItem } = useCart();
  const cat = categoryLabels[product.category] || {};
  const disc = product.discount_percent > 0;
  const quickVariant = product.variants[0];
  const unavailable = !quickVariant.in_stock;

  const quickAdd = () => {
    if (unavailable) return;
    addItem({
      lookup_key: quickVariant.lookup_key,
      name: product.name,
      format: quickVariant.format,
      price: quickVariant.discounted_price ?? quickVariant.price,
      image: quickVariant.image || product.image,
      quantity: 1,
    });
  };

  return (
    <article
      data-testid={`product-card-${product.slug}`}
      className="group"
    >
      <div className="relative overflow-hidden bg-white aspect-[4/5] border border-stone-200">
        <Link to={`/product/${product.slug}`} className="block h-full" aria-label={tl(product.name)}>
          <img
            src={product.image}
            alt={tl(product.name)}
            className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
          />
        </Link>
        {disc && (
          <span
            data-testid={`product-badge-${product.slug}`}
            className="absolute top-3 left-3 z-10 bg-red-700 text-white text-xs font-semibold uppercase tracking-[0.12em] px-3 py-2 shadow-sm"
          >
            -{product.discount_percent}% {t("product.sale")}
          </span>
        )}
        <button
          type="button"
          onClick={quickAdd}
          disabled={unavailable}
          data-testid={`quick-add-${product.slug}`}
          aria-label={`${t("product.addToCart")}: ${tl(product.name)}, ${tl(quickVariant.format)}`}
          className="absolute bottom-3 right-3 z-10 flex h-12 w-12 items-center justify-center bg-champagne text-charcoal shadow-sm transition-colors hover:bg-charcoal hover:text-white disabled:cursor-not-allowed disabled:bg-stone-300 disabled:text-stone-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-charcoal"
        >
          <Plus size={24} strokeWidth={1.5} aria-hidden="true" />
        </button>
      </div>
      <Link to={`/product/${product.slug}`} className="block pt-4 min-w-0">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.18em] text-stone-400">
            {product.family ? tl(product.family) : tl(cat)}
          </p>
          <h3 className="font-serif text-lg text-charcoal leading-snug mt-1">{tl(product.name)}</h3>
          {fragranceAudience(product) && <span className="mt-2 inline-block border border-stone-200 px-2 py-1 text-[10px] uppercase tracking-wider text-stone-600">{tl(fragranceAudience(product))}</span>}
        </div>
        <p className="text-sm pt-2 flex flex-wrap items-baseline gap-x-1.5 gap-y-1">
          {product.variants.length > 1 && (
            <span className="text-stone-400 text-xs mr-1">{t("shop.from")}</span>
          )}
          {disc ? (
            <>
              <span className="line-through text-stone-400">{formatPrice(product.price_from)}</span>
              <span className="text-charcoal font-medium">{formatPrice(product.discounted_from)}</span>
            </>
          ) : (
            <span className="text-charcoal">{formatPrice(product.price_from)}</span>
          )}
        </p>
        {!product.in_stock && <p className="mt-2 text-xs uppercase tracking-[0.12em] text-red-700">Esaurito</p>}
      </Link>
    </article>
  );
};

export default ProductCard;
